import { Calendar, Filter, Download, RefreshCw, CalendarIcon, X } from 'lucide-react';
import { exportToCSV } from '../../../utils/exportHelpers';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Button } from "../../../components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "../../../components/ui/toggle-group";
import { Calendar as CalendarComponent } from "../../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import { format } from "date-fns";
import { useState, useEffect } from "react";
import { cn } from "../../../lib/utils";

export function FilterBar({
  dateRange,
  setDateRange,
  orderTypeFilter,
  setOrderTypeFilter,
  viewMode,
  setViewMode,
  onRefresh,
  dashboardData
}) {
  const [selectedPreset, setSelectedPreset] = useState('today');
  const [customDateRange, setCustomDateRange] = useState({
    from: new Date(),
    to: new Date()
  });

  // Sync with parent
  useEffect(() => {
    if (typeof dateRange === 'string') {
      setSelectedPreset(dateRange);
    } else if (dateRange?.type === 'custom') {
      setSelectedPreset('custom');
      setCustomDateRange({
        from: dateRange.startDate || new Date(),
        to: dateRange.endDate || new Date()
      });
    }
  }, [dateRange]);

  const handleExport = () => {
    if (dashboardData) {
      const timestamp = new Date().toISOString().split('T')[0];
      exportToCSV(dashboardData, `dashboard-export-${timestamp}`);
    }
  };

  const handleRefresh = () => {
    onRefresh?.();
  };

  const handlePresetChange = (value) => {
    setSelectedPreset(value);

    if (value === 'custom') {
      setDateRange({
        type: 'custom',
        startDate: customDateRange.from,
        endDate: customDateRange.to
      });
    } else {
      setDateRange(value);
      setTimeout(() => onRefresh?.(), 100);
    }
  };

  const clearCustomRange = () => {
    setSelectedPreset('today');
    setDateRange('today');
    setCustomDateRange({
      from: new Date(),
      to: new Date()
    });
    setTimeout(() => onRefresh?.(), 100);
  };

  const getDisplayText = () => {
    if (customDateRange?.from) {
      if (customDateRange?.to) {
        return `${format(customDateRange.from, 'LLL dd, y')} - ${format(customDateRange.to, 'LLL dd, y')}`;
      }
      return format(customDateRange.from, 'LLL dd, y');
    }
    return 'Pick a date range';
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4 border border-slate-200">
      <div className="flex flex-wrap items-center gap-3">

        {/* Date Range */}
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#01796d]" />

          <Select value={selectedPreset} onValueChange={handlePresetChange}>
            <SelectTrigger className="w-[140px] bg-slate-50 border-slate-200">
              <SelectValue placeholder="Select date range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>

          {selectedPreset === 'custom' && (
            <>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal bg-slate-50 border-slate-200 min-w-[260px]"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {getDisplayText()}
                  </Button>
                </PopoverTrigger>

                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="range"
                    defaultMonth={customDateRange?.from}
                    selected={customDateRange}
                    numberOfMonths={2}
                    onSelect={(range) => {
                      if (!range) return;

                      setCustomDateRange({
                        from: range.from ?? customDateRange.from,
                        to: range.to ?? customDateRange.to,
                      });

                      if (range.from && range.to) {
                        setDateRange({
                          type: "custom",
                          startDate: range.from,
                          endDate: range.to,
                        });

                        setTimeout(() => onRefresh?.(), 100);
                      }
                    }}
                    classNames={{
                      day_today: "bg-teal-300 text-teal-700",
                      day_selected:
                        "bg-[#01796d] text-white hover:bg-[#01695f] focus:bg-[#01695f]",
                      day_range_start:
                        "bg-[#01695f] text-white rounded-l-md",
                      day_range_end:
                        "bg-[#01695f] text-white rounded-r-md",
                      day_range_middle:
                        "bg-teal-500 text-white",
                    }}
                  />
                </PopoverContent>
              </Popover>

              <Button
                variant="ghost"
                size="icon"
                onClick={clearCustomRange}
                className="h-8 w-8 text-slate-500 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>

        {/* Order Type */}
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-[#01796d]" />

          <Select value={orderTypeFilter} onValueChange={setOrderTypeFilter}>
            <SelectTrigger className="w-[140px] bg-slate-50 border-slate-200">
              <SelectValue placeholder="Filter by order type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orders</SelectItem>
              <SelectItem value="dinein">Dine-In</SelectItem>
              <SelectItem value="delivery">Delivery</SelectItem>
              <SelectItem value="pickup">Pickup</SelectItem>
              <SelectItem value="takeaway">Takeaway</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1" />

        {/* Actions */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          className="flex items-center gap-2 bg-slate-50 border-slate-200"
        >
          <RefreshCw className="w-4 h-4" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>

        <Button
          size="sm"
          onClick={handleExport}
          className="flex items-center gap-2 bg-gradient-to-r from-[#01796d] to-teal-600 text-white"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline">Export</span>
        </Button>

      </div>
    </div>
  );
}